/** The shell reads a logo without constructing the Settings record schemas.
 * The existing pharmacy wire re-exports this read for its other consumers. */
import { createApiClient } from "@/api/client";
import type { paths } from "@/api/generated/pharmos-api";
import { AppError, isAppError } from "@/api/problems";

const LOGO = "/v1/branches/{branchCode}/logo" as const;

function client() {
  return createApiClient<paths>();
}

/**
 * The branch's logo file. An empty body is an empty blob. A 404 is the same
 * answer: the live API has no `logo.png` yet, and Upload is how the first
 * file gets there. Any other failure stays a failure, so the dialogue does
 * not offer a write it cannot read back.
 */
export async function readBranchLogo(branchCode: string): Promise<Blob> {
  try {
    const { data, response } = await client().GET(LOGO, {
      params: { path: { branchCode } },
      headers: { Accept: "application/octet-stream" },
      parseAs: "blob",
    });
    if (!(data instanceof Blob)) {
      throw new AppError("contract", {
        status: response.status,
        message: `${LOGO} did not return application/octet-stream or image/png`,
      });
    }
    return data;
  } catch (error) {
    if (isAppError(error) && error.status === 404) return new Blob();
    throw error;
  }
}
